import { useDepartmentText } from 'model/DepartmentTerminology';
import { Badge, Group, Stack, Tooltip } from '@mantine/core';
import P2Regular from 'components/typography/P2Regular';
import P3Regular from 'components/typography/P3Regular';
import { ReactNode } from 'react';

export interface DepartmentBreadcrumbItem {
  departmentId: number;
  departmentName: string;
  hierarchyLevelSlug?: string;
  levelOrder: number;
  levelLabel: string;
}

interface DepartmentBreadcrumbProps {
  breadcrumbs: DepartmentBreadcrumbItem[];
  compact?: boolean;
  inactive?: boolean;
  leftSection?: ReactNode;
  tooltipDetails?: ReactNode;
  withPathInTooltip?: boolean;
}

/**
 * Canonical root-to-node department breadcrumb used by tables, selects and detail surfaces.
 */
export default function DepartmentBreadcrumb({
  breadcrumbs,
  compact = false,
  inactive = false,
  leftSection,
  tooltipDetails,
  withPathInTooltip = true,
}: DepartmentBreadcrumbProps) {
  const departmentText = useDepartmentText();
  const path = breadcrumbs.map(({ departmentName }) => departmentName).join(' / ');
  const node = breadcrumbs[breadcrumbs.length - 1];
  const levelBadge = node && (
    <Badge
      component="span"
      data-testid="department-level-badge"
      size="xs"
      styles={{
        root: {
          display: 'inline-block',
          height: 'auto',
          maxWidth: '100%',
          overflow: 'visible',
          textOverflow: 'clip',
          lineHeight: 'normal',
          paddingBlock: 2,
          verticalAlign: 'middle',
        },
        inner: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', overflowWrap: 'anywhere' },
      }}
    >
      {node.levelLabel}
    </Badge>
  );

  if (compact) {
    return (
      <Tooltip
        label={
          tooltipDetails && withPathInTooltip ? (
            <Stack spacing={6}>
              <P3Regular>{path || departmentText('Bez položky úrovne', 'Bez strediska')}</P3Regular>
              {tooltipDetails}
            </Stack>
          ) : tooltipDetails ? (
            tooltipDetails
          ) : (
            path || departmentText('Bez položky úrovne', 'Bez strediska')
          )
        }
        events={{ hover: true, focus: true, touch: true }}
        multiline
        withinPortal
      >
        <Group
          aria-label={`${departmentText('Položka úrovne', 'Stredisko')} ${
            node?.departmentName ?? departmentText('Bez položky úrovne', 'Bez strediska')
          }`}
          spacing={6}
          noWrap
          tabIndex={0}
        >
          {leftSection}
          <P2Regular color={inactive ? 'gray.6' : 'gray.8'} data-testid="department-breadcrumb-label">
            <span>{node?.departmentName ?? departmentText('Bez položky úrovne', 'Bez strediska')}</span>
            {levelBadge && <> {levelBadge}</>}
          </P2Regular>
          {inactive && <Badge color="gray">Vyradené</Badge>}
        </Group>
      </Tooltip>
    );
  }

  return (
    <Group spacing={6} noWrap>
      <P2Regular color={inactive ? 'gray.6' : 'gray.8'} data-testid="department-breadcrumb-label">
        <span>{path || departmentText('Bez položky úrovne', 'Bez strediska')}</span>
        {levelBadge && <> {levelBadge}</>}
      </P2Regular>
      {inactive && <Badge color="gray">Vyradené</Badge>}
    </Group>
  );
}

/** Returns the stable full path used by quick search, exports and selects. */
export const formatDepartmentPath = (breadcrumbs: DepartmentBreadcrumbItem[]) =>
  breadcrumbs.map(({ departmentName }) => departmentName).join(' / ');
